// BILLING-PLANS: money and percent inputs as exact integers. The API speaks integer cents and
// basis points; a typed "12.34" becomes 1234 by string arithmetic, never `* 100` on a float
// (0.29 * 100 is 28.999…). Commas as thousands separators and a leading "$" are allowed.

const DOLLARS = /^\$?\s*(\d{1,3}(?:,\d{3})+|\d+)?(?:\.(\d{0,2}))?$/;

/** "12.34" → 1234, "1,500" → 150000, "0.1" → 10; null for anything else ("1.234", "-1", ""). */
export function parseDollarsToCents(text: string): number | null {
  const s = text.trim();
  if (s === "" || s === "$" || s === ".") return null;
  const m = DOLLARS.exec(s);
  if (!m) return null;
  const whole = (m[1] ?? "").replace(/,/g, "");
  const frac = m[2] ?? "";
  if (whole === "" && frac === "") return null;
  const cents = Number(whole || "0") * 100 + Number(frac.padEnd(2, "0") || "0");
  return Number.isSafeInteger(cents) ? cents : null;
}

/** 1234 → "12.34", 150000 → "1500" (an input's value; no currency sign, no grouping). */
export function centsToDollarsText(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  const whole = Math.floor(abs / 100);
  const frac = abs % 100;
  return frac === 0 ? `${sign}${whole}` : `${sign}${whole}.${String(frac).padStart(2, "0")}`;
}

const PERCENT = /^(\d+)?(?:\.(\d{0,2}))?\s*%?$/;

/** "30" → 3000, "12.5" → 1250, "0.01" → 1 (basis points); null past two decimals or 100. */
export function parsePercentToBp(text: string): number | null {
  const s = text.trim();
  if (s === "" || s === "%") return null;
  const m = PERCENT.exec(s);
  if (!m) return null;
  const whole = m[1] ?? "";
  const frac = m[2] ?? "";
  if (whole === "" && frac === "") return null;
  const bp = Number(whole || "0") * 100 + Number(frac.padEnd(2, "0") || "0");
  return bp <= 10_000 ? bp : null;
}

/** 3000 → "30", 1250 → "12.5". */
export function bpToPercentText(bp: number): string {
  const whole = Math.floor(bp / 100);
  const frac = bp % 100;
  if (frac === 0) return String(whole);
  return `${whole}.${String(frac).padStart(2, "0").replace(/0$/, "")}`;
}
